<template>
  <section class="activity-timeline" dir="rtl" aria-label="">
    <ol v-if="requestActivities.length" class="activity-timeline__list">
      <li v-for="activity in requestActivities" :key="activity.key"
          class="activity-timeline__item" :class="`activity-timeline__item--${activity.type}`">
        <span class="activity-timeline__avatar" aria-hidden="true">
          <i :class="activity.type === 'note' ? 'fa-solid fa-lock' : 'fa-solid fa-user'"></i>
        </span>
        <article class="activity-timeline__card">
          <div class="activity-timeline__header">
            <div class="activity-timeline__labels">
              <span class="activity-timeline__badge">{{ activity.label }}</span>
              <span v-if="activity.type === 'note'" class="activity-timeline__private">
                <i class="fa-solid fa-lock" aria-hidden="true"></i> فقط برای کارشناسان
              </span>
            </div>
            <span class="activity-timeline__date">
              <i class="fa-regular fa-calendar" aria-hidden="true"></i>
              <time :datetime="activity.datetime">{{ activity.date }}</time>
            </span>
          </div>
          <div class="activity-timeline__meta">
            <span class="activity-timeline__author"><slot name="author" :user="activity.user" :author="activity.author">{{ activity.author }}</slot></span>
            <span v-if="activity.type !== 'note'" class="activity-timeline__source">
              <i :class="activity.type === 'requester' ? 'fa-solid fa-globe' : 'fa-solid fa-desktop'" aria-hidden="true"></i>
              {{ activity.type === 'requester' ? 'پرتال' : 'پنل کارشناسی' }}
            </span>
          </div>
          <div class="activity-timeline__body" v-html="activity.body"></div>
          <slot name="attachments" :attachments="activity.attachments"></slot>
        </article>
      </li>
    </ol>
    <div v-else class="activity-timeline__empty">
      <i class="fa-regular fa-comments" aria-hidden="true"></i>
      <span>هنوز پاسخ یا یادداشتی ثبت نشده است.</span>
    </div>
  </section>
</template>

<script>
import moment from 'moment-jalaali'

export default {
  name: 'RequestActivityTimeline',
  props: {
    answers: {type: Array, default: () => []},
    notes: {type: Array, default: () => []}
  },
  computed: {
    requestActivities() {
      const mapItems = (items, type, field, label) => (Array.isArray(items) ? items : [])
          .filter(item => item && typeof item === 'object')
          .map((item, index) => {
            // Support the API spelling and the alternate ClubMemeber spelling.
            const isPortal = type === 'answer' && ['ClubMember', 'ClubMemeber'].includes(item.user?.type)
            return {
              key: `${type}-${item.id ?? 'item'}-${index}`,
              type: isPortal ? 'requester' : type,
              label: isPortal ? 'پاسخ درخواست‌کننده' : label,
              author: item.user?.name || (isPortal ? 'درخواست‌کننده' : 'کارشناس'),
              user: item.user,
              body: item[field] || '',
              attachments: Array.isArray(item.attachments) ? item.attachments : [],
              ...this.formatActivityDate(item.created_at)
            }
          })
      return [
        ...mapItems(this.answers, 'answer', 'answer', 'پاسخ کارشناس'),
        ...mapItems(this.notes, 'note', 'note', 'یادداشت داخلی')
      ]
    },
  },
  methods: {
    formatActivityDate(value) {
      if (typeof value !== 'string' || !value.trim()) return {date: '—', datetime: undefined}
      const timestamp = value.trim()
      if (!/^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(timestamp)) {
        return {date: '—', datetime: undefined}
      }
      const parsed = moment(timestamp)
      if (!parsed.isValid()) return {date: '—', datetime: undefined}
      const hasTime = /[T ]\d{2}:\d{2}/.test(value)
      const formatted = parsed.format(hasTime ? 'jYYYY/jMM/jDD · HH:mm' : 'jYYYY/jMM/jDD')
      return {
        date: formatted.replace(/\d/g, digit => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]),
        datetime: parsed.format(hasTime ? 'YYYY-MM-DDTHH:mm:ssZ' : 'YYYY-MM-DD')
      }
    },
  }
}
</script>

<style scoped lang="scss">
.activity-timeline {
  width: 100%;
  min-width: 0;
  margin: 24px 0;
  text-align: right;
  color: #334155;
  container-type: inline-size;

  &, *, *::before, *::after { box-sizing: border-box; }
  &__list { list-style: none; margin: 0; padding: 0; }
  &__item {
    --accent: #3b82f6;
    --surface: #f3f8ff;
    --border: #dce9fa;
    --badge: #e2eeff;
    --label: #2563b0;
    position: relative;
    padding: 0 52px 16px 0;
    min-width: 0;
    &::before {
      content: '';
      position: absolute;
      top: 42px;
      right: 17px;
      bottom: -18px;
      width: 2px;
      background: #e8edf3;
    }
    &:last-child { padding-bottom: 0; &::before { display: none; } }
    &--requester {
      --accent: #94a3b8;
      --surface: #fcfdff;
      --border: #e5e9ef;
      --badge: #edf1f5;
      --label: #526174;
    }
    &--note {
      --accent: #e4a51b;
      --surface: #fffbf0;
      --border: #f1e6c7;
      --badge: #fbefc8;
      --label: #8a6415;
    }
  }
  &__avatar {
    position: absolute;
    top: 18px;
    right: 0;
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    border: 3px solid #fff;
    border-radius: 50%;
    background: var(--accent);
    color: #fff;
    font-size: 14px;
  }
  &__card {
    min-width: 0;
    padding: 18px 20px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    box-shadow: 0 2px 5px rgb(15 23 42 / 2%);
  }
  &__header, &__labels, &__meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; min-width: 0; }
  &__header { justify-content: space-between; }
  &__badge { padding: 5px 10px; border-radius: 7px; background: var(--badge); color: var(--label); font-size: 13px; font-weight: 700; line-height: 1.8; }
  &__private, &__date, &__source { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; line-height: 1.8; color: #64748b; }
  &__private { color: #8a7440; }
  &__date { flex-wrap: wrap; }
  &__date time { direction: ltr; unicode-bidi: isolate; overflow-wrap: anywhere; }
  &__meta { margin-top: 12px; }
  &__author { font-size: 13px; font-weight: 600; overflow-wrap: anywhere; min-width: 0; }
  &__source { border-inline-start: 1px solid var(--border); padding-inline-start: 12px; }
  &__body {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid var(--border);
    font-size: 15px;
    line-height: 1.95;
    overflow-wrap: anywhere;
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
    :deep(p) { margin: 0 0 8px; color: inherit; font-size: inherit; line-height: inherit; }
    :deep(p:last-child) { margin-bottom: 0; }
    :deep(img), :deep(video), :deep(iframe) { max-width: 100%; border-radius: 8px; }
    :deep(img), :deep(video) { height: auto; }
    :deep(pre) { white-space: pre-wrap; overflow-wrap: anywhere; }
    :deep(a) { color: #2563b0; text-decoration: underline; }
  }
  &__empty { display: flex; justify-content: center; align-items: center; gap: 10px; padding: 24px 16px; border: 1px dashed #dce3ec; border-radius: 12px; background: #fafbfd; color: #64748b; font-size: 13px; line-height: 1.8; }
}

@container (max-width: 520px) {
  .activity-timeline {
    &__item { padding-right: 38px; &::before { right: 13px; } }
    &__avatar { width: 28px; height: 28px; font-size: 11px; top: 16px; }
    &__card { padding: 14px 12px; border-radius: 12px; }
    &__header { align-items: flex-start; flex-direction: column; gap: 8px; }
    &__labels { gap: 6px 8px; }
    &__badge { font-size: 12px; }
    &__private, &__date, &__source { font-size: 11px; }
    &__body { font-size: 14px; margin-top: 12px; padding-top: 12px; }
    &__meta { gap: 6px 10px; }
    &__source { padding-inline-start: 10px; }
  }
}
</style>
